import { useEffect, useMemo, useState } from "react";
import { Modal, Form, Input, Select, InputNumber, DatePicker, Switch, Button, ConfigProvider } from "antd";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import customizeRequiredMark from "../../../../components/custom_antd/CustomizeRequiredMark";

const { TextArea } = Input;

const MODAL_CLASS_NAMES = {
    content:
        "!w-[calc(100vw-2rem)] !max-w-[calc(100vw-2rem)] sm:!w-[560px] sm:!max-w-[560px] md:!w-[640px] md:!max-w-[640px]",
    body: "max-h-[min(80vh,720px)] overflow-y-auto px-0 pt-2 sm:px-0",
};

const FieldLabel = ({ children }) => (
    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">{children}</p>
);

FieldLabel.propTypes = {
    children: PropTypes.node.isRequired,
};

const DetailRow = ({ label, value, className = "" }) => (
    <div className={`min-w-0 ${className}`}>
        <FieldLabel>{label}</FieldLabel>
        <div className="mt-1 text-sm font-medium text-[#2F2F46]">{value ?? "—"}</div>
    </div>
);

DetailRow.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.node,
    className: PropTypes.string,
};

const StatusBadge = ({ active, t }) => (
    <span
        className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
            active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"
        }`}>
        {active ? t("couponCode.modal.active") : t("couponCode.modal.inactive")}
    </span>
);

StatusBadge.propTypes = {
    active: PropTypes.bool.isRequired,
    t: PropTypes.func.isRequired,
};

const CouponCodeViewBody = ({ couponData, t }) => {
    const isPercentage = couponData?.discountType === "PERCENTAGE";
    const discountTypeLabel = isPercentage
        ? t("couponCode.modal.discountTypePercentage")
        : t("couponCode.modal.discountTypeFixed");

    const discountValueDisplay = isPercentage
        ? `${couponData?.discountValue ?? 0}%`
        : `$${couponData?.discountValue ?? 0}`;

    const maxDiscountDisplay =
        isPercentage && couponData?.maxDiscountValue != null && couponData.maxDiscountValue !== ""
            ? `$${couponData.maxDiscountValue}`
            : "—";

    const expiryDisplay = couponData?.expiryDate
        ? new Date(couponData.expiryDate).toLocaleDateString()
        : t("couponCode.modal.noExpiry");

    const maxUsesDisplay =
        couponData?.maxUses != null && couponData.maxUses !== ""
            ? String(couponData.maxUses)
            : t("couponCode.modal.unlimitedUses");

    const usageDisplay = `${couponData?.usedCount ?? 0} / ${couponData?.maxUses ?? "∞"}`;

    return (
        <div className="space-y-5">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 pb-4">
                <div className="min-w-0 flex-1">
                    <FieldLabel>{t("couponCode.modal.code")}</FieldLabel>
                    <p className="mt-1 break-all text-xl font-bold tracking-tight text-[#5D5DF6]">
                        {couponData?.code || "—"}
                    </p>
                </div>
                <StatusBadge active={Boolean(couponData?.isActive)} t={t} />
            </div>

            <div className="border-b border-gray-100 pb-4">
                <FieldLabel>{t("couponCode.modal.description")}</FieldLabel>
                <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-[#2F2F46]">
                    {couponData?.description || "—"}
                </p>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <DetailRow label={t("couponCode.modal.discountType")} value={discountTypeLabel} />
                <DetailRow label={t("couponCode.modal.discountValue")} value={discountValueDisplay} />
                {isPercentage ? (
                    <DetailRow label={t("couponCode.modal.maxDiscountValue")} value={maxDiscountDisplay} />
                ) : null}
                <DetailRow label={t("couponCode.modal.expiryDate")} value={expiryDisplay} />
                <DetailRow label={t("couponCode.modal.maxUses")} value={maxUsesDisplay} />
                <DetailRow label={t("couponCode.modal.usage")} value={usageDisplay} />
            </div>
        </div>
    );
};

CouponCodeViewBody.propTypes = {
    couponData: PropTypes.object,
    t: PropTypes.func.isRequired,
};

const CouponCodeModal = ({ open, mode, couponData, onCancel, onSubmit, loading }) => {
    const { t } = useTranslation("settings");
    const [form] = Form.useForm();
    const [discountType, setDiscountType] = useState("FIXED");
    const [isActive, setIsActive] = useState(true);

    const modalTitle = useMemo(() => {
        if (mode === "create") return t("couponCode.modal.createTitle");
        if (mode === "update") return t("couponCode.modal.updateTitle");
        return t("couponCode.modal.viewTitle");
    }, [mode, t]);

    useEffect(() => {
        if (open) {
            if (mode === "create") {
                form.resetFields();
                setDiscountType("FIXED");
                setIsActive(true);
            } else if ((mode === "update" || mode === "view") && couponData) {
                form.setFieldsValue({
                    code: couponData.code,
                    description: couponData.description,
                    discountType: couponData.discountType || "FIXED",
                    discountValue: couponData.discountValue,
                    maxDiscountValue: couponData.maxDiscountValue,
                    expiryDate: couponData.expiryDate ? dayjs(couponData.expiryDate) : null,
                    maxUses: couponData.maxUses,
                    isActive: couponData.isActive !== undefined ? couponData.isActive : true,
                });
                setDiscountType(couponData.discountType || "FIXED");
                setIsActive(couponData.isActive !== undefined ? couponData.isActive : true);
            }
        } else {
            form.resetFields();
        }
    }, [open, mode, couponData, form]);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            const submitData = {
                ...values,
                expiryDate: values.expiryDate ? values.expiryDate.toISOString() : null,
                isActive,
            };
            await onSubmit(submitData);
        } catch (_error) {
            // validation handled by form
        }
    };

    const handleCancel = () => {
        form.resetFields();
        onCancel();
    };

    const isViewMode = mode === "view";
    const isCreateMode = mode === "create";

    const footer = isViewMode ? (
        <div className="flex justify-stretch sm:justify-end">
            <Button shape="round" className="!h-11 w-full sm:!h-auto sm:w-auto" onClick={handleCancel}>
                {t("close")}
            </Button>
        </div>
    ) : (
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
                variant="outlined"
                color="danger"
                shape="round"
                className="!h-11 sm:!h-auto"
                onClick={handleCancel}
                disabled={loading}>
                {t("cancel")}
            </Button>
            <Button
                type="primary"
                shape="round"
                className="!h-11 sm:!h-auto"
                onClick={handleSubmit}
                loading={loading}>
                {isCreateMode ? t("couponCode.create") : t("save")}
            </Button>
        </div>
    );

    return (
        <Modal
            title={modalTitle}
            open={open}
            onCancel={handleCancel}
            footer={footer}
            centered
            destroyOnHidden
            classNames={MODAL_CLASS_NAMES}>
            {isViewMode ? (
                <CouponCodeViewBody couponData={couponData} t={t} />
            ) : (
                <div className="space-y-4">
                    <div className="flex items-center justify-between gap-4 rounded-xl border border-gray-100 bg-[#F8F8FC] px-4 py-3">
                        <div>
                            <p className="text-sm font-semibold text-[#2F2F46]">{t("couponCode.modal.status")}</p>
                            <p className="text-xs text-gray-500">
                                {isActive ? t("couponCode.modal.active") : t("couponCode.modal.inactive")}
                            </p>
                        </div>
                        <ConfigProvider theme={{ token: { colorPrimary: "#36d649" } }}>
                            <Switch
                                checkedChildren={t("couponCode.modal.active")}
                                unCheckedChildren={t("couponCode.modal.inactive")}
                                onChange={setIsActive}
                                checked={isActive}
                            />
                        </ConfigProvider>
                    </div>

                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleSubmit}
                        requiredMark={customizeRequiredMark}
                        className="[&_.ant-form-item]:mb-4">
                        <Form.Item
                            label={t("couponCode.modal.code")}
                            name="code"
                            rules={[{ required: true, message: "Please enter coupon code" }]}>
                            <Input
                                placeholder="Enter coupon code"
                                disabled={!isCreateMode}
                                className="!h-11 !rounded-xl"
                            />
                        </Form.Item>

                        <Form.Item
                            label={t("couponCode.modal.description")}
                            name="description"
                            rules={[{ required: true, message: "Please enter description" }]}>
                            <TextArea rows={3} placeholder="Enter coupon description" className="!rounded-xl" />
                        </Form.Item>

                        <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
                            <Form.Item
                                label={t("couponCode.modal.discountType")}
                                name="discountType"
                                rules={[{ required: true, message: "Please select discount type" }]}>
                                <Select
                                    placeholder="Select discount type"
                                    className="[&_.ant-select-selector]:!h-11 [&_.ant-select-selector]:!rounded-xl"
                                    onChange={(value) => {
                                        setDiscountType(value);
                                        if (value === "FIXED") {
                                            form.setFieldsValue({ maxDiscountValue: 0 });
                                        }
                                    }}
                                    options={[
                                        { label: "Fixed Amount ($)", value: "FIXED" },
                                        { label: "Percentage (%)", value: "PERCENTAGE" },
                                    ]}
                                />
                            </Form.Item>

                            <Form.Item
                                label={t("couponCode.modal.discountValue")}
                                name="discountValue"
                                rules={[
                                    { required: true, message: "Please enter discount value" },
                                    {
                                        type: "number",
                                        min: 1,
                                        message: "Discount value must be greater than or equal to 1",
                                    },
                                ]}>
                                <InputNumber
                                    className="!w-full [&_.ant-input-number-input]:!h-11 [&_.ant-input-number]:!w-full [&_.ant-input-number]:!rounded-xl"
                                    placeholder="Enter discount value"
                                    min={0}
                                    addonAfter={discountType === "PERCENTAGE" ? "%" : "$"}
                                />
                            </Form.Item>
                        </div>

                        <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
                            {discountType === "PERCENTAGE" ? (
                                <Form.Item
                                    label={t("couponCode.modal.maxDiscountValue")}
                                    name="maxDiscountValue"
                                    rules={[
                                        {
                                            type: "number",
                                            min: 1,
                                            message: "Max discount value must be greater than or equal to 1",
                                        },
                                    ]}>
                                    <InputNumber
                                        className="!w-full [&_.ant-input-number-input]:!h-11 [&_.ant-input-number]:!w-full [&_.ant-input-number]:!rounded-xl"
                                        placeholder="Optional"
                                        min={0}
                                        addonAfter="$"
                                    />
                                </Form.Item>
                            ) : (
                                <div className="hidden sm:block" aria-hidden />
                            )}

                            <Form.Item label={t("couponCode.modal.expiryDate")} name="expiryDate">
                                <DatePicker
                                    className="!h-11 !w-full !rounded-xl"
                                    placeholder="Select expiry date"
                                    format="YYYY-MM-DD"
                                    disabledDate={(current) => current && current < dayjs().startOf("day")}
                                />
                            </Form.Item>
                        </div>

                        <Form.Item
                            label={t("couponCode.modal.maxUses")}
                            name="maxUses"
                            rules={[
                                { required: true, message: "Please enter max uses" },
                                { type: "number", min: 1, message: "Max uses must be greater than 0" },
                            ]}>
                            <InputNumber
                                className="!w-full [&_.ant-input-number-input]:!h-11 [&_.ant-input-number]:!w-full [&_.ant-input-number]:!rounded-xl sm:!max-w-[50%]"
                                placeholder="Enter max uses"
                                min={1}
                            />
                        </Form.Item>
                    </Form>
                </div>
            )}
        </Modal>
    );
};

CouponCodeModal.propTypes = {
    open: PropTypes.bool.isRequired,
    mode: PropTypes.oneOf(["create", "update", "view"]).isRequired,
    couponData: PropTypes.object,
    onCancel: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    loading: PropTypes.bool,
};

export default CouponCodeModal;
